import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Shield, Copyright, Crosshair, Folder, ThumbsUp, Clock, User } from 'lucide-react';
import SEO from './SEO';
import MyCleanStudioHeader from './MyCleanStudioHeader';
import MyCleanStudioFooter from './MyCleanStudioFooter';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import './HomePage.css';

function HomePage() {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const handleCreateAccount = () => {
    navigate('/signup');
  };

  const handlePlatformCardClick = (selectedPlatform) => {
    navigate('/dashboard/new-check', {
      state: { selectedPlatform },
    });
  };

  return (
    <>
      <SEO
        title="myCleanStudio - Content Compliance Checker for Creators"
        description="Check your videos and content against YouTube and TikTok platform guidelines before publishing. AI-powered visual scanning and music detection to keep your content compliant and monetized."
        keywords="content compliance, YouTube guidelines, TikTok guidelines, video compliance checker, copyright detection, content moderation, creator tools, video analysis, platform compliance"
        url="/"
      />
      <div className="homepage-root">
        {/* Header */}
        <MyCleanStudioHeader />

        {/* Hero Section */}
        <section className="homepage-hero">
          <div className="homepage-hero-content">
            <div className="homepage-hero-left">
              <p className="homepage-subheading">CONTENT CREATOR TOOL</p>
              <h2 className="homepage-title">Keep Your Content Monetization-Ready</h2>
              <p className="homepage-description">
                Check your content against platform guidelines before publishing. Avoid demonetization and ensure your content aligns with YouTube, TikTok, and Instagram policies.
              </p>
              <div className="homepage-cta-buttons">
                <button className="homepage-cta-primary" onClick={() => navigate('/dashboard/new-check')}>Check Your Content</button>
                {!isAuthenticated && (
                  <button className="homepage-cta-secondary" onClick={handleCreateAccount}>
                    <User size={18} />
                    Create Free Account
                  </button>
                )}
              </div>
              <p className="homepage-disclaimer">No credit card required. Free credits to get started.</p>
            </div>
            <div className="homepage-hero-right">
              <div className="homepage-video-container">
                <iframe
                  src="https://player.vimeo.com/video/1159678200?h=054b4fdaf5&badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="MCS workflow"
                  className="homepage-video-iframe"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="homepage-features-section">
          <div className="homepage-features-container">
            <p className="homepage-features-label">FEATURES</p>
            <h3 className="homepage-features-title">Everything You Need For Content Compliance</h3>
            <p className="homepage-features-subtitle">
              Check your content against multiple platform guidelines before publishing.
            </p>
            <div className="homepage-features-grid">
              <div className="homepage-feature-card">
                <div className="homepage-feature-icon">
                  <Shield size={32} />
                </div>
                <h4 className="homepage-feature-title">Multi-Platform Scanning</h4>
                <p className="homepage-feature-text">
                  Scan your content against YouTube, TikTok, and Instagram guidelines simultaneously.
                </p>
              </div>

              <div className="homepage-feature-card">
                <div className="homepage-feature-icon">
                  <Copyright size={32} />
                </div>
                <h4 className="homepage-feature-title">Copyright Detection</h4>
                <p className="homepage-feature-text">
                  Identify potential copyright issues in your audio before uploading.
                </p>
              </div>

              <div className="homepage-feature-card">
                <div className="homepage-feature-icon">
                  <Crosshair size={32} />
                </div>
                <h4 className="homepage-feature-title">Precise Issue Location</h4>
                <p className="homepage-feature-text">
                  Pinpoint exactly where issues occur in your content for easy editing.
                </p>
              </div>

              <div className="homepage-feature-card">
                <div className="homepage-feature-icon">
                  <Folder size={32} />
                </div>
                <h4 className="homepage-feature-title">Video Library</h4>
                <p className="homepage-feature-text">
                  Store your videos for re-scanning against different platform guidelines.
                </p>
              </div>

              <div className="homepage-feature-card">
                <div className="homepage-feature-icon">
                  <ThumbsUp size={32} />
                </div>
                <h4 className="homepage-feature-title">Smart Recommendations</h4>
                <p className="homepage-feature-text">
                  Get actionable recommendations to improve your content compliance.
                </p>
              </div>

              <div className="homepage-feature-card">
                <div className="homepage-feature-icon">
                  <Clock size={32} />
                </div>
                <h4 className="homepage-feature-title">Analysis History</h4>
                <p className="homepage-feature-text">
                  Build and access your complete analysis history over time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Supported Platforms */}
        <section className="homepage-platforms-section">
          <div className="homepage-platforms-container">
            <p className="homepage-platforms-label">PLATFORMS</p>
            <h3 className="homepage-platforms-title">Check Content For All Major Platforms</h3>
            <p className="homepage-platforms-subtitle">
              Ensure your content meets the guidelines of the platforms you publish to.
            </p>
            <div className="homepage-platforms-grid">
              <div
                className="homepage-platform-card"
                role="button"
                tabIndex={0}
                onClick={() => handlePlatformCardClick('YouTube')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePlatformCardClick('YouTube');
                  }
                }}
              >
                <div className="homepage-platform-icon">
                  <img src={youtubeIcon} alt="YouTube" />
                </div>
                <h4 className="homepage-platform-name">YouTube</h4>
                <p className="homepage-platform-description">
                  Music copyright detection and community guideline violation checks.
                </p>
              </div>

              <div
                className="homepage-platform-card"
                role="button"
                tabIndex={0}
                onClick={() => handlePlatformCardClick('TikTok')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePlatformCardClick('TikTok');
                  }
                }}
              >
                <div className="homepage-platform-icon">
                  <img src={tiktokIcon} alt="TikTok" />
                </div>
                <h4 className="homepage-platform-name">TikTok</h4>
                <p className="homepage-platform-description">
                  Ensure content meets TikTok's community standards.
                </p>
              </div>

              <div
                className="homepage-platform-card"
                role="button"
                tabIndex={0}
                onClick={() => handlePlatformCardClick('Instagram')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePlatformCardClick('Instagram');
                  }
                }}
              >
                <div className="homepage-platform-icon instagram">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h4 className="homepage-platform-name">Instagram</h4>
                <p className="homepage-platform-description">
                  Verify content aligns with Instagram's content policies.
                </p>
              </div>

              <div
                className="homepage-platform-card"
                role="button"
                tabIndex={0}
                onClick={() => handlePlatformCardClick('Facebook')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePlatformCardClick('Facebook');
                  }
                }}
              >
                <div className="homepage-platform-icon facebook">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <h4 className="homepage-platform-name">Facebook</h4>
                <p className="homepage-platform-description">
                  Check content against Facebook's community standards.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <MyCleanStudioFooter />
      </div>
    </>
  );
}

export default HomePage;
